import { days, textDays } from '@shared-constants/date-constants';
import { BasicInterfaceTypeEnum } from '@shared-enums/cats.enum';
import { camelize } from '@shared-helpers/string-number-helpers';
import { IDayOfWeek } from '@shared-interfaces/dates';

import { IDefaultValueObject } from '../../components/modal/modal-content-default-change/modal-content-default-change.component';
import { IConfigType, ISelectOptions, ITableConfigEntity, ITableConfigOptions } from '../../interfaces/internal-interfaces';

export function makeSelectOption(value: string, description: string): ISelectOptions {
  return {
    value: value,
    description: description,
  };
}

export function makeConfigItem(
  header: string,
  type: IConfigType,
  columns: number,
  shouldSpanAllRows: boolean,
  isReadOnly: boolean,
): ITableConfigEntity {
  return {
    header,
    key: camelize(header),
    type,
    columns,
    shouldSpanAllRows,
    isReadOnly,
  };
}

// eslint-disable-next-line complexity  -- There is no good way to simplify this or split it up. There are a lot of nullish-coalescing things, but its simple to understand what is happening
export function makeComplexConfigItem<K extends string | object = string>(
  header: string,
  key: K extends string ? string : keyof K, //either a plain string, or a key of the object passed to the generic
  type: IConfigType,
  columns: number,
  shouldSpanAllRows: boolean,
  isReadOnly: boolean,
  options?: ITableConfigOptions,
): ITableConfigEntity<K extends string ? string : keyof K> {
  return {
    header: header,
    key: key,
    type: type,
    columns: columns,
    shouldSpanAllRows: shouldSpanAllRows,
    isReadOnly: isReadOnly,
    selectOptions: options?.selectOptions ?? [],
    shouldTotal: options?.shouldTotal ?? false,
    tooltip: options?.tooltip ?? '',
    hasInlineLabel: options?.hasInlineLabel,
    icon: options?.icon ?? '',
    imageHeight: options?.imageHeight ?? 0,
    imageWidth: options?.imageWidth ?? 0,
    svgIcon: options?.svgIcon,
    shouldDetectClick: options?.shouldDetectClick ?? false,
    shouldDetectEnter: options?.shouldDetectEnter ?? false,
    shouldShowEntityTooltip: options?.shouldShowEntityTooltip ?? false,
    shouldDetectHeaderClick: options?.shouldDetectHeaderClick ?? false,
    clickValue: options?.clickValue ?? undefined,
    displayKey: options?.displayKey ?? undefined,
    isRequired: options?.isRequired ?? false,
    class: options?.class ?? '',
    headerClass: options?.headerClass ?? '',
    isDisabled: options?.isDisabled ?? false,
    maxTextLength: options?.maxTextLength ?? 524288,
    minTextRows: options?.minTextRows ?? 1,
    maxTextRows: options?.maxTextRows ?? 2,
    shouldRemoveMatStyling: options?.shouldRemoveMatStyling ?? true,
    nestedObjectName: options?.nestedObjectName ?? undefined,
    shouldTextRemoveZerosFromNonTotalRow: options?.shouldTextRemoveZerosFromNonTotalRow ?? false,
  };
}

export function makeDefaultRemapConfig(billcodes: ISelectOptions[], type: BasicInterfaceTypeEnum): ITableConfigEntity[] {
  const config = [
    makeComplexConfigItem<IDefaultValueObject>('Current', 'current', 'left-text', 3, false, true, {
      class: 'bg-lightgray pl-2 h-full border-b border-b-white whitespace-pre-wrap text-wrap',
    }),
    makeComplexConfigItem<IDefaultValueObject>('New', 'new', 'select', 3, false, false, {
      selectOptions: billcodes,
      class: 'flex h-full items-center justify-center',
    }),
  ];
  if (type !== BasicInterfaceTypeEnum.Equipment) {
    config.unshift(
      makeComplexConfigItem<IDefaultValueObject>('', 'label', 'bold-text', 1, false, true, {
        class: '!text-base !text-inherit h-full',
      }),
    );
  }
  return config;
}

export function getConfigByKey(array: ITableConfigEntity[], key: string) {
  return array.find((x) => x.key === key);
}

export function makeDaysConfig(
  type: IConfigType,
  columns: number,
  shouldShowBulkAdd?: boolean,
  cssClass?: string,
  tooltip?: string,
): ITableConfigEntity<IDayOfWeek>[] {
  const arr: ITableConfigEntity<IDayOfWeek>[] = [];
  days.forEach((element, index) => {
    const configEntity: ITableConfigEntity<IDayOfWeek> = {
      header: element.substring(0, 2),
      key: element.toLowerCase() as Lowercase<IDayOfWeek>,
      type: type,
      tooltip,
      shouldSpanAllRows: false,
      columns,
      selectOptions: null,
      isReadOnly: false,
      shouldTotal: true,
      shouldDetectHeaderClick: !!shouldShowBulkAdd,
      dayOfWeek: index,
      class: cssClass,
    };
    arr.push(configEntity);
  });
  return arr;
}

export function makeTextDaysConfig(type: IConfigType): ITableConfigEntity<IDayOfWeek>[] {
  const arr: ITableConfigEntity<IDayOfWeek>[] = [];
  textDays.forEach((element) => {
    const configEntity: ITableConfigEntity<IDayOfWeek> = {
      header: element,
      key: element.toLowerCase() as IDayOfWeek,
      type: type,
      shouldSpanAllRows: false,
      columns: 1,
      selectOptions: null,
      isReadOnly: false,
      shouldTotal: false,
    };
    arr.push(configEntity);
  });
  return arr;
}
